/**
 * Module valuation-details composes economic-unit schemas and defines report text, overall
 * summaries, XML metadata, and plausibility assessments.
 *
 * Method parameters and calculation results are defined in the corresponding valuation
 * method modules.
 */
import { z } from 'zod';
import { UUIDSchema } from './common';
import { MonetaryValueSchema, SquareMetreMoneyPerAreaValueSchema } from './money';
import { MarketDataItemSchema, ValuationComparablesSchema } from './valuation-comparables';
import { CombinedComparisonValuationSchema, ComparisonValuationParametersSchema } from './valuation-comparison';
import { CombinedCostValuationSchema, CostValuationParametersSchema } from './valuation-cost';
import { CombinedIncomeValuationSchema, ReadIncomeValuationParametersSchema, TenantIncomeValuationParametersSchema, } from './valuation-income';
import { CombinedLeaseholdValuationSchema, LeaseholdResultsSchema, LeaseholdSchema } from './valuation-leasehold';
import { ValuationMethodSchema } from './valuation-primitives';
import { BaseValuationStructureSchema, CombinedLandValuationSchema, EncumbranceSchema, SpecialPropertyFeatureSchema, ValuationLandSchema, ValuationStructureSchema, } from './valuation-property';
import { TenantSchema, ValuationTenantSummarySchema } from './valuation-tenants';
export const TextblockSchema = z.object({
    type: z.string(),
    text: z.string(),
});
export const SwotAnalysisSchema = z.object({
    strengths: z.string().array(),
    weaknesses: z.string().array(),
    opportunities: z.string().array(),
    threats: z.string().array(),
});
const SummaryRiskQualitySchema = z.enum([
    'excellent',
    'very_good',
    'good',
    'above_average',
    'slightly_above_average',
    'average',
    'below_average',
    'mediocre',
    'bad',
    'catastrophic',
]);
const SummaryRentSituationSchema = z.enum([...SummaryRiskQualitySchema.options, 'slightly_below_average', '']);
const ReusabilitySchema = z.enum(['given', 'partially_given', 'not_given']);
const LoanRiskSchema = z.enum([
    'very_high',
    'high',
    'above_average',
    'slightly_above_average',
    'average',
    'slightly_below_average',
    'below_average',
    'low',
    'very_low',
]);
const SummaryRentalStatusSchema = z.enum(['self_used', 'rented', 'partly_self_used', 'partly_rented', 'self_used_and_rented', 'vacant']);
export const SummaryRisksSchema = z.object({
    rentability: SummaryRiskQualitySchema.optional(),
    usability: SummaryRiskQualitySchema.optional(),
    reusability: ReusabilitySchema.optional(),
    loan_risk: LoanRiskSchema.optional(),
    rental_status: SummaryRentalStatusSchema.optional(),
    rent_situation: SummaryRentSituationSchema.optional(),
});
export const ValXmlFileSchema = z.object({
    uuid: UUIDSchema,
    name: z.string(),
    category: z.literal('val_xml_file'),
    url: z.string(),
    content_type: z.literal('application/xml'),
});
const ValuationResultSchema = z.object({
    valuation_method: ValuationMethodSchema,
    value: MonetaryValueSchema,
    value_with_leasehold_deduction: MonetaryValueSchema,
    is_custom: z.boolean(),
    rounded: MonetaryValueSchema,
    value_per_sqm: SquareMetreMoneyPerAreaValueSchema,
});
const ValuationSummarySchema = z.object({
    market_valuation_results: ValuationResultSchema.array(),
    mortgage_valuation_results: ValuationResultSchema.array(),
    chosen_market_valuation_method: ValuationMethodSchema.optional(),
    chosen_mortgage_lending_valuation_method: ValuationMethodSchema.optional(),
    market_value: MonetaryValueSchema.optional(),
    fictitious_full_ownership_market_value: MonetaryValueSchema.optional(),
    market_value_per_sqm: SquareMetreMoneyPerAreaValueSchema.optional(),
    mortgage_value: MonetaryValueSchema.optional(),
    fictitious_full_ownership_mortgage_value: MonetaryValueSchema.optional(),
    mortgage_value_per_sqm: SquareMetreMoneyPerAreaValueSchema.optional(),
});
const PlausibilityAssessmentSchema = z.enum([
    'assessment_above',
    'assessment_upper',
    'assessment_middle',
    'assessment_lower',
    'assessment_below',
]);
const PlausibilitySchema = z.object({
    assessment: PlausibilityAssessmentSchema.optional(),
    criteria: z.string().array(),
});
export const ExternalValuationEconomicUnitSchema = z.object({
    structures: BaseValuationStructureSchema.array(),
    tenantsV3: TenantSchema.array(),
    income_valuation_parameters: TenantIncomeValuationParametersSchema,
});
const EconomicUnitTextBlockSchema = TextblockSchema.extend({
    type: z.enum(['construction_costs', 'depreciation', 'exterior_costs', 'additional_costs', 'market_adjustment', 'regional_adjustment']),
});
export const ValuationEconomicUnitSchema = z.object({
    market_valuation_methods: ValuationMethodSchema.array(),
    mortgage_lending_valuation_methods: ValuationMethodSchema.array(),
    special_property_features: SpecialPropertyFeatureSchema.array(),
    encumbrances: EncumbranceSchema.array(),
    encumbrances_value: MonetaryValueSchema,
    structures: ValuationStructureSchema.array(),
    land: ValuationLandSchema,
    cost_valuation_parameters: CostValuationParametersSchema,
    income_valuation_parameters: ReadIncomeValuationParametersSchema,
    comparison_valuation_parameters: ComparisonValuationParametersSchema,
    land_value: MonetaryValueSchema,
    summary_comment: z.string(),
    summary: ValuationSummarySchema,
    land_valuation: CombinedLandValuationSchema,
    comparison_valuation: CombinedComparisonValuationSchema,
    cost_valuation: CombinedCostValuationSchema,
    income_valuation: CombinedIncomeValuationSchema,
    tenant_summary: ValuationTenantSummarySchema,
    leasehold_valuation: CombinedLeaseholdValuationSchema,
    user_defined_market_value: MonetaryValueSchema.optional(),
    user_defined_mortgage_lending_value: MonetaryValueSchema.optional(),
    chosen_market_valuation_method: ValuationMethodSchema,
    chosen_mortgage_lending_valuation_method: ValuationMethodSchema,
    market_data: MarketDataItemSchema.array().optional(),
    comparables: ValuationComparablesSchema.optional(),
    text_blocks: EconomicUnitTextBlockSchema.array(),
    leasehold: LeaseholdSchema.optional(),
    leasehold_results: LeaseholdResultsSchema.optional(),
    plausibility: PlausibilitySchema.optional(),
    structure_description: z.string().optional(),
    structure_change_from_previous_valuation: z.string().optional(),
    special_property_features_change_from_previous_valuation: z.string().optional(),
    land_value_change_from_previous_valuation: z.string().optional(),
    result_plausibility: z.string().optional(),
    result_change_from_previous_valuation: z.string().optional(),
    area_values_gross_area_text: z.string().optional(),
    area_values_usage_area_text: z.string().optional(),
});
